import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Package } from 'lucide-react';

import { GenericLoading } from 'components/ui/Loader';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { NsKey } from 'enums/NsKey';

import {
  GetPlateformeCatalogueResponse,
  getPlateformeCatalogue
} from '../api/catalogueApi';
import { PlateformeCatalogueManage } from './PlateformeCatalogueManage';

interface PlateformeCatalogueTabProps {
  plateformeId: string;
}

/**
 * "Catalogue" tab of the plateforme details page, in two views: Consultation
 * (read-only — produits attributed to the plateforme, each with the tunnels it
 * distributes, everything navigates to the pages) and Gestion (master/detail
 * attribution). Rendered as GenericTabs like the personne morale fiche — the
 * inactive view is unmounted, so switching reloads fresh data.
 */
export const PlateformeCatalogueTab: React.FC<PlateformeCatalogueTabProps> = ({
  plateformeId
}) => {
  const { t } = useTranslation(NsKey.Common);

  return (
    <div className="flex flex-col gap-4">
      {/* Même en-tête que les autres onglets (Rôles, Habilitations tunnels). */}
      <h2 className="flex items-center gap-3 text-3xl font-semibold text-zinc-800">
        <Package className="w-7 h-7 text-primary" />
        {t('PLATEFORME_CATALOGUE.TAB')}
      </h2>

      <GenericTabs defaultTab="consult" fullWidth={false}>
        <GenericTabs.Item label={t('PLATEFORME_CATALOGUE.VIEW_CONSULT')} value="consult" />
        <GenericTabs.Item label={t('PLATEFORME_CATALOGUE.VIEW_MANAGE')} value="manage" />

        <GenericTabs.Content value="consult">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {t('PLATEFORME_CATALOGUE.DESCRIPTION')}
            </p>
            <PlateformeCatalogueConsult plateformeId={plateformeId} />
          </div>
        </GenericTabs.Content>

        <GenericTabs.Content value="manage">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-500">
              {t('PLATEFORME_CATALOGUE.MANAGE_DESCRIPTION')}
            </p>
            <PlateformeCatalogueManage plateformeId={plateformeId} />
          </div>
        </GenericTabs.Content>
      </GenericTabs>
    </div>
  );
};

/**
 * Read-only view: the produits attributed to the plateforme, each with the
 * tunnels it distributes (business rule: tunnel plateformes ⊆ produit
 * plateformes, so every tunnel finds its produit here).
 */
const PlateformeCatalogueConsult: React.FC<PlateformeCatalogueTabProps> = ({
  plateformeId
}) => {
  const { t } = useTranslation(NsKey.Common);
  const navigate = useNavigate();
  const [catalogue, setCatalogue] =
    useState<GetPlateformeCatalogueResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getPlateformeCatalogue(plateformeId)
      .then(data => {
        if (!cancelled) setCatalogue(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [plateformeId]);

  if (error) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
        {t('PLATEFORME_CATALOGUE.LOAD_ERROR')}
      </div>
    );
  }

  if (!catalogue) {
    return <GenericLoading loading />;
  }

  if (catalogue.produits.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
        {t('PLATEFORME_CATALOGUE.EMPTY')}
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {catalogue.produits.map(produit => {
          const actifs = produit.tunnels.filter(tu => tu.isActive).length;
          return (
            <div
              key={produit.produitId}
              className="flex flex-col rounded-xl border border-gray-100 bg-white shadow-sm"
            >
              <div className="flex flex-1 flex-col gap-4 p-5">
                <div className="flex items-start justify-between gap-3">
                  {/* L'en-tête mène à la page du produit, comme les chips aux tunnels. */}
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/back-office/produits/${produit.produitId}`)
                    }
                    className="group flex min-w-0 flex-1 items-start gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Package size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-bold text-gray-900 group-hover:text-primary">
                        {produit.label}
                      </span>
                      <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-0.5 font-mono text-[11px] font-semibold text-gray-500">
                        {produit.code}
                      </span>
                    </span>
                  </button>
                  {!produit.isLinkActive && (
                    <span className="flex-shrink-0 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                      {t('PLATEFORME_CATALOGUE.INACTIVE')}
                    </span>
                  )}
                </div>

                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    {t('PLATEFORME_CATALOGUE.TUNNELS_OVERLINE')}
                  </p>
                  {produit.tunnels.length === 0 ? (
                    <p className="mt-2 text-xs text-gray-400">
                      {t('PLATEFORME_CATALOGUE.NO_TUNNELS')}
                    </p>
                  ) : (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {produit.tunnels.map(tunnel => (
                        <button
                          key={tunnel.tunnelId}
                          type="button"
                          onClick={() =>
                            navigate(`/back-office/tunnels/${tunnel.tunnelId}`)
                          }
                          title={tunnel.code}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary/40 hover:bg-primary/5 ${
                            tunnel.isActive
                              ? 'border-gray-200 text-gray-700'
                              : 'border-gray-200 text-gray-400'
                          }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${
                              tunnel.isActive ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          />
                          {tunnel.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
                <span className="text-xs text-gray-500">
                  {produit.tunnels.length === 0
                    ? t('PLATEFORME_CATALOGUE.NO_TUNNELS')
                    : t('PLATEFORME_CATALOGUE.ACTIVE_COUNT', {
                        count: actifs,
                        total: produit.tunnels.length
                      })}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/back-office/produits/${produit.produitId}`)
                  }
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 transition-colors hover:text-primary"
                >
                  {t('PLATEFORME_CATALOGUE.OPEN_PRODUCT')}
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
